import { optionsProps, PairGuestMember, PairGuestRow, PairRsvpStatus } from 'types';

export type PairGuestFilterKey =
  | 'menteeRsvp'
  | 'mentorRsvp'
  | 'pairRsvpStatus'
  | 'mentorAttended'
  | 'menteeAttended'
  | 'pairAttended';

export type PairGuestFilters = Record<PairGuestFilterKey, string[]>;

export interface PairGuestFilterGroup {
  key: PairGuestFilterKey;
  label: string;
  options: optionsProps[];
}

export const EMPTY_PAIR_GUEST_FILTERS: PairGuestFilters = {
  menteeRsvp: [],
  mentorRsvp: [],
  pairRsvpStatus: [],
  mentorAttended: [],
  menteeAttended: [],
  pairAttended: [],
};

export const rsvpOptions: optionsProps[] = [
  { label: 'Yes', value: 'Approved' },
  { label: 'No', value: 'Declined' },
  { label: 'Waiting', value: 'Pending' },
];

export const attendedOptions: optionsProps[] = [
  { label: 'Yes', value: 'Attended' },
  { label: 'No', value: 'Did Not Attend' },
];

export const PAIR_RSVP_FILTER_GROUPS: PairGuestFilterGroup[] = [
  { key: 'menteeRsvp', label: 'Mentee RSVP', options: rsvpOptions },
  { key: 'mentorRsvp', label: 'Mentor RSVP', options: rsvpOptions },
  {
    key: 'pairRsvpStatus',
    label: 'Pair RSVP Status',
    options: [
      { label: 'Both Attending', value: 'Both Attending' },
      { label: 'Not Meeting', value: 'Not Meeting' },
      { label: 'Waiting for RSVP(s)', value: 'Waiting for RSVP(s)' },
    ],
  },
];

export const PAIR_ATTENDANCE_FILTER_GROUPS: PairGuestFilterGroup[] = [
  { key: 'mentorAttended', label: 'Mentor Attended', options: attendedOptions },
  { key: 'menteeAttended', label: 'Mentee Attended', options: attendedOptions },
  {
    key: 'pairAttended',
    label: 'Pair Attended',
    options: [
      { label: 'Yes', value: 'Pair Met' },
      { label: 'No', value: 'Pair Did Not Meet' },
    ],
  },
];

export const pairGuestFilterGroups = (isAttendanceFinalized: boolean) =>
  isAttendanceFinalized ? PAIR_ATTENDANCE_FILTER_GROUPS : PAIR_RSVP_FILTER_GROUPS;

// Only the groups the table is currently filtering by, so a server-side consumer (the CSV export)
// matches the rows on screen.
export const pairGuestFiltersPayload = (filters: PairGuestFilters, isAttendanceFinalized: boolean) =>
  Object.fromEntries(pairGuestFilterGroups(isAttendanceFinalized).map(({ key }) => [key, filters[key]]));

const rsvpValue = (member: PairGuestMember | null) => {
  if (!member) {
    return null;
  }
  const status = member.event?.status;
  return status === 'Approved' || status === 'Declined' ? status : 'Pending';
};

const attendedValue = (member: PairGuestMember | null) => {
  if (!member) {
    return null;
  }
  return member.event?.attendance === 'Attended' ? 'Attended' : 'Did Not Attend';
};

const filterValue = (
  row: PairGuestRow,
  key: PairGuestFilterKey,
  pairStatusFor: (row: PairGuestRow) => PairRsvpStatus | null
) => {
  switch (key) {
    case 'menteeRsvp':
      return rsvpValue(row?.Mentee);
    case 'mentorRsvp':
      return rsvpValue(row?.Mentor);
    case 'menteeAttended':
      return attendedValue(row?.Mentee);
    case 'mentorAttended':
      return attendedValue(row?.Mentor);
    case 'pairRsvpStatus':
    case 'pairAttended':
      return pairStatusFor(row);
  }
};

export const filterPairGuestRows = (
  rows: PairGuestRow[] | undefined,
  filters: PairGuestFilters,
  isAttendanceFinalized: boolean,
  pairStatusFor: (row: PairGuestRow) => PairRsvpStatus | null
) => {
  const activeGroups = pairGuestFilterGroups(isAttendanceFinalized).filter(({ key }) => filters[key].length > 0);
  if (!rows || activeGroups.length === 0) {
    return rows;
  }
  return rows.filter(row =>
    activeGroups.every(({ key }) => {
      const value = filterValue(row, key, pairStatusFor);
      return value !== null && filters[key].includes(value);
    })
  );
};
